import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input, Select } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

export const SA_BANKS = [
  'Absa',
  'African Bank',
  'Bidvest',
  'Capitec',
  'Discovery Bank',
  'FNB',
  'Investec',
  'Nedbank',
  'Standard Bank',
  'TymeBank',
] as const;

export const bankAccountSchema = z.object({
  accountHolderName: z.string().min(2, 'Required'),
  bank: z.string().min(2, 'Pick your bank'),
  accountNumber: z.string().min(6, 'Min 6 digits').max(20),
  branchCode: z.string().min(4, 'Min 4 digits').max(10),
});

export type BankAccountValues = z.infer<typeof bankAccountSchema>;

type Props = {
  onSubmit: (values: BankAccountValues) => void | Promise<void>;
  submitting?: boolean;
  submitLabel?: string;
  showSubmit?: boolean;
  formId?: string;
};

export function BankAccountForm({
  onSubmit,
  submitting,
  submitLabel = 'Save bank details',
  showSubmit = true,
  formId,
}: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BankAccountValues>({
    resolver: zodResolver(bankAccountSchema),
  });

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <Input
        label="Account holder"
        autoComplete="name"
        {...register('accountHolderName')}
        error={errors.accountHolderName?.message}
      />
      <Select
        label="Bank"
        {...register('bank')}
        error={errors.bank?.message}
        options={[{ value: '', label: 'Select…' }, ...SA_BANKS.map((b) => ({ value: b, label: b }))]}
      />
      <Input
        label="Account number"
        inputMode="numeric"
        autoComplete="off"
        {...register('accountNumber')}
        error={errors.accountNumber?.message}
      />
      <Input
        label="Branch code"
        inputMode="numeric"
        {...register('branchCode')}
        error={errors.branchCode?.message}
      />
      <p className="text-xs text-makhe-muted/80">
        We only store the last 4 digits. Your full number is used for payouts, never shown publicly.
      </p>
      {showSubmit && (
        <Button type="submit" loading={submitting} className="w-full">
          {submitLabel}
        </Button>
      )}
    </form>
  );
}

/** Validate bank fields without mounting the full form (onboarding finish button). */
export function parseBankAccount(values: BankAccountValues): BankAccountValues {
  return bankAccountSchema.parse(values);
}
